<template lang="pug">
.FYoutubeVideo(:style="style")
  iframe(
    v-if="videoId"
    ref="iframeRef"
    :src="iframeSrc"
    :allow="iframeAllowedAttributes"
    height="100%"
    width="100%"
    frameborder="0"
    loading="lazy"
    allowfullscreen
  )
</template>

<style lang="stylus">
.FYoutubeVideo
  position relative
  overflow hidden
  border-radius rem(16)
  aspect-ratio var(--FYoutubeVideo--aspectRatio)
</style>

<script setup lang="ts">
export interface FYoutubeVideoProps {
  /**
   * The youtube video id
   * @example You need to catch the code after `v=` for www.youtube.com urls, or after the `/` for `youtu.be` urls:
   * - For https://www.youtube.com/watch?v=KM1DiWwdHjM, the code is `KM1DiWwdHjM`
   * - For https://youtu.be/daIiHd2kDs0, the code is `daIiHd2kDs0`
   */
  videoId: string;
  /**
   * Whether to automatically play the video on load
   */
  autoplay?: boolean;
  /**
   * Aspect ratio of the video
   */
  aspectRatio?: `${number}/${number}`;
  /**
   * Start of the video in seconds
   */
  videoStartTime?: number;
}

const props = withDefaults(defineProps<FYoutubeVideoProps>(), {
  autoplay: false,
  aspectRatio: '16/9',
  videoStartTime: 0,
});

const emit = defineEmits<{
  /**
   * Emitted when the video ends
   */
  (event: 'ended'): void;
}>();

const iframeBaseSrc = computed(
  () =>
    // The base url 'www.youtube-nocookie.com' ensures the embeded iframe *tracks less* the user with third party cookies
    // (it still places undocummented cookies and uses the local-storage to track the user, but it's better than nothing)
    `https://www.youtube-nocookie.com/embed/${props.videoId}`
);

const iframeQueryString = computed(() =>
  Object.entries({
    autoplay: Number(props.autoplay),
    color: 'white',
    iv_load_policy: 0,
    playsinline: 1,
    enablejsapi: 1,
    rel: 0,
    start: props.videoStartTime,
  })
    .map(([key, value]) => `${key}=${value}`)
    .join('&')
);

const iframeSrc = computed(
  () => `${iframeBaseSrc.value}?${iframeQueryString.value}`
);

const iframeAllowedAttributes = [
  'accelerometer',
  'autoplay',
  'encrypted-media',
  'gyroscope',
  'picture-in-picture',
].join('; ');

const style = computed<Style>(() => ({
  '--FYoutubeVideo--aspectRatio': props.aspectRatio,
}));

const iframeRef = ref<HTMLIFrameElement>();
const player = ref<YT.Player>();

onMounted(() => {
  window.onYouTubeIframeAPIReady = function () {
    if (!iframeRef.value) return;
    player.value = new YT.Player(iframeRef.value, {
      events: {
        onStateChange: event => {
          const isEnded = event.data === YT.PlayerState.ENDED;
          if (isEnded) emit('ended');
        },
      },
    });
  };
});

onUnmounted(() => {
  if (player.value) player.value.destroy();
});

useHead({
  script: [
    {
      src: 'https://www.youtube.com/iframe_api',
      async: true,
      defer: true,
    },
  ],
});
</script>
